/**
 * GoAutoradio product integration for the Alma application block.
 * The block still supplies the merchant, SDK URL and payment-plan settings.
 * Amounts come from the current variant and complementary product prices in cents.
 */
(() => {
  function start() {
    const widget = document.querySelector(window.ALMA_WIDGET_DIV_NAME || '#alma-widget');
    const information = widget?.closest('.product-information');
    if (!widget || !information || !window.ALMA_PAGE_TYPE?.startsWith('product')) return;
    if (window.Shopify?.currency?.active !== 'EUR' || widget.dataset.goAlmaInitialized) return;
    widget.dataset.goAlmaInitialized = 'true';

    const product = window.ALMA_PRODUCT_DATA;
    const options = information.querySelector('complementary-product-options');
    let controller;
    let lastAmount;
    let refreshTimer;
    let plans;

    function readPlans() {
      const configuration = JSON.parse(window.ALMA_FEE_PLANS);
      return Object.entries(configuration).flatMap(([key, value]) => {
        const match = key.match(/_(\d+)_(\d+)_(\d+)$/);
        if (value.is_enabled !== 'True' || !match) return [];
        const minAmount = Number(value.min_amount);
        const maxAmount = Number(value.max_amount);
        if (!Number.isSafeInteger(minAmount) || !Number.isSafeInteger(maxAmount)) {
          throw Error('Invalid Alma plan limits');
        }
        return [{installmentsCount: Number(match[1]), deferredDays: Number(match[2]),
          deferredMonth: Number(match[3]), minAmount, maxAmount}];
      });
    }

    function amountInCents() {
      const formVariant = information.querySelector('input[name="id"]')?.value;
      const urlVariant = new URLSearchParams(window.location.search).get('variant');
      const variantId = formVariant || urlVariant;
      const variant = variantId
        ? product?.variants?.find(value => String(value.id) === String(variantId))
        : product?.variants?.find(value => value.available) || product?.variants?.[0];
      if (!variant || !Number.isSafeInteger(variant.price) || variant.price < 0) {
        throw Error('Invalid selected product price');
      }
      let unitAmount = variant.price;
      if (options) {
        const data = JSON.parse(options.querySelector('script')?.textContent || '{}');
        for (const option of options.querySelectorAll('.option-value-container[data-active]')) {
          const price = data.option_prices?.[option.dataset.variantId];
          if (!Number.isSafeInteger(price) || price < 0) throw Error('Invalid selected option price');
          unitAmount += price;
        }
      }
      const input = information.querySelector('input[name="quantity"]');
      const quantity = Number(input?.value || '1');
      if (!Number.isSafeInteger(quantity) || quantity < 1) throw Error('Invalid selected quantity');
      const amount = unitAmount * quantity;
      if (!Number.isSafeInteger(amount)) throw Error('Invalid pack total');
      return amount;
    }

    function refresh() {
      if (!controller) return;
      try {
        const amount = amountInCents();
        if (amount === lastAmount && !widget.hidden) return;
        controller.add(window.Alma.Widgets.PaymentPlans, {
          container: window.ALMA_WIDGET_DIV_NAME || '#alma-widget',
          locale: window.Shopify.locale,
          purchaseAmount: amount,
          hideIfNotEligible: !window.ALMA_DISPLAY_WIDGET_NOT_ELIGIBLE,
          plans,
          monochrome: window.ALMA_WIDGET_MONOCHROME,
        });
        lastAmount = amount;
        widget.dataset.goAlmaAmount = String(amount);
        delete widget.dataset.goAlmaError;
        widget.hidden = false;
      } catch (error) {
        // Do not show a stale payment quote when a selected price cannot be read.
        widget.hidden = true;
        widget.dataset.goAlmaError = 'invalid-amount';
        lastAmount = undefined;
        console.warn('GoAutoradio Alma: unable to refresh selected pack.', error);
      }
    }

    function scheduleRefresh() {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(refresh, 40);
    }

    if (options) {
      new MutationObserver(scheduleRefresh).observe(options, {
        subtree: true, attributes: true, attributeFilter: ['data-active'],
      });
    }
    information.addEventListener('change', event => {
      if (event.target.matches('input[name="quantity"], input[name="id"]')) scheduleRefresh();
    });
    document.addEventListener('product:variant-change', scheduleRefresh);

    function initialize() {
      try {
        plans = readPlans();
        if (!plans.length) return;
        controller = window.Alma.Widgets.initialize(window.ALMA_MERCHANT_ID, window.Alma.ApiMode.LIVE);
        refresh();
      } catch (error) {
        widget.hidden = true;
        widget.dataset.goAlmaError = 'initialization';
        console.warn('GoAutoradio Alma: unable to initialize payment plans.', error);
      }
    }

    if (window.Alma?.Widgets) {
      initialize();
    } else {
      const existing = Array.from(document.scripts).find(script => script.src === window.ALMA_WIDGET_CDN_URL);
      const sdk = existing || document.createElement('script');
      sdk.addEventListener('load', initialize, {once: true});
      sdk.addEventListener('error', () => {
        widget.hidden = true;
        widget.dataset.goAlmaError = 'sdk-load';
      }, {once: true});
      if (!existing) {
        sdk.src = window.ALMA_WIDGET_CDN_URL;
        sdk.async = true;
        document.head.appendChild(sdk);
      }
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, {once: true});
  } else {
    start();
  }
})();
